import type { ListingMenuKind } from '@pulsify/shared/dto'
import { motion, useReducedMotion } from 'framer-motion'
import * as m from '~/paraglide/messages'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { fileKey } from '../lib/pick-files'
import { readingProgressLabel } from '../lib/reading-progress'
import { FileThumb } from './menu-file-picker'

const SWEEP_SECONDS = 2.4

interface MenuReadingStageProps {
  kind: ListingMenuKind
  files: File[]
  dishes: number | null
  onCancel: () => void
}

export function MenuReadingStage({ kind, files, dishes, onCancel }: MenuReadingStageProps) {
  const reduced = useReducedMotion()

  return (
    <Card className="flex flex-col gap-6 p-6">
      <h2 className="font-semibold font-serif text-2xl text-foreground tracking-tight">
        {kind === 'lunch' ? m.menuReadingLunchTitle() : m.menuReadingFullTitle()}
      </h2>

      <div className="relative overflow-hidden rounded-xl border bg-muted/40 p-3">
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
          {files.map((file) => (
            <li key={fileKey(file)}>
              <FileThumb file={file} />
            </li>
          ))}
        </ul>
        {!reduced && (
          <motion.span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 h-1/3"
            style={{
              background:
                'linear-gradient(to bottom, transparent, color-mix(in srgb, var(--brand) 22%, transparent) 50%, transparent)',
            }}
            initial={{ top: '-35%' }}
            animate={{ top: ['-35%', '100%'] }}
            transition={{
              duration: SWEEP_SECONDS,
              repeat: Number.POSITIVE_INFINITY,
              ease: [0.45, 0, 0.55, 1],
            }}
          />
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div role="status" aria-live="polite" className="flex items-center gap-2">
          <PulsifyLoader className="size-6" />
          <span className="font-medium text-foreground tabular-nums">
            {readingProgressLabel(dishes)}
          </span>
        </div>
        <Button type="button" variant="outline" onClick={onCancel}>
          {m.cancel()}
        </Button>
      </div>
      <p className="text-muted-foreground text-sm">{m.menuReadingHint()}</p>
    </Card>
  )
}
